import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Pagination, QueryErrorAlert, Table } from '@shared/components';
import { formatHistoryActor } from '@/features/agreements/utils/formatHistoryActor';
import type { CustomerAmendment, CustomerAmendmentsPage } from '../../types/customers.types';
import {
    amendmentFieldLabel,
    amendmentTypeLabel,
    evidenceTypeLabel,
    formatRecordedAt,
    nameChangeReasonLabel,
    requesterTypeLabel,
} from '../../utils/amendmentLabels';

interface Props {
    isLoading: boolean;
    isError: boolean;
    page: CustomerAmendmentsPage | undefined;
    currentPage: number;
    onRetry: () => void;
    onPageChange: (page: number) => void;
}

const headingId = 'customer-amendment-log-heading';

const EMPTY = '—';

const labelOrEmpty = (value: string | null, label: (value: string) => string): string =>
    value ? label(value) : EMPTY;

// Every value is rendered as React text, so stored markup cannot execute.
const COLUMNS: ColumnDef<CustomerAmendment>[] = [
    {
        id: 'recordedAt',
        header: 'When',
        cell: ({ row }) => (
            <time dateTime={row.original.recordedAt} className="whitespace-nowrap">
                {formatRecordedAt(row.original.recordedAt)}
            </time>
        ),
    },
    {
        id: 'amendmentType',
        header: 'Change',
        cell: ({ row }) => amendmentTypeLabel(row.original.amendmentType),
    },
    {
        id: 'changes',
        header: 'Before and after',
        cell: ({ row }) => (
            <ul className="space-y-1">
                {row.original.changedFields.map((field) => (
                    <li key={field}>
                        <span className="font-medium">{amendmentFieldLabel(field)}:</span>{' '}
                        <span className="text-mp-muted">
                            {row.original.oldValues[field] || '(empty)'}
                        </span>{' '}
                        <span aria-hidden="true">&rarr;</span>
                        <span className="sr-only">changed to</span> {row.original.newValues[field] || '(empty)'}
                    </li>
                ))}
            </ul>
        ),
    },
    {
        id: 'reason',
        header: 'Reason',
        cell: ({ row }) => labelOrEmpty(row.original.reason, nameChangeReasonLabel),
    },
    {
        id: 'requesterType',
        header: 'Requested by',
        cell: ({ row }) => labelOrEmpty(row.original.requesterType, requesterTypeLabel),
    },
    {
        id: 'evidenceType',
        header: 'Evidence seen',
        cell: ({ row }) => labelOrEmpty(row.original.evidenceType, evidenceTypeLabel),
    },
    {
        id: 'actor',
        header: 'Changed by',
        cell: ({ row }) => formatHistoryActor(row.original) ?? EMPTY,
    },
];

const getRowId = (amendment: CustomerAmendment) => amendment.id;

/** Presentational. Order is the server's (newest first), so sorting is off. */
export function CustomerAmendmentLog({ isLoading, isError, page, currentPage, onRetry, onPageChange }: Props) {
    return (
        <section aria-labelledby={headingId} className="space-y-4">
            <h3
                id={headingId}
                className="text-xs font-semibold uppercase tracking-wide text-mp-muted"
            >
                Change history
            </h3>
            <LogBody isLoading={isLoading} isError={isError} page={page} onRetry={onRetry} />
            {page && page.meta.lastPage > 1 && (
                <Pagination
                    currentPage={currentPage}
                    lastPage={page.meta.lastPage}
                    total={page.meta.total}
                    perPage={page.meta.perPage}
                    onPageChange={onPageChange}
                />
            )}
        </section>
    );
}

function LogBody({ isLoading, isError, page, onRetry }: Pick<Props, 'isLoading' | 'isError' | 'page' | 'onRetry'>) {
    if (isLoading) {
        return <LoadingSpinner message="Loading change history" />;
    }

    if (isError || !page) {
        return (
            <QueryErrorAlert message="Failed to load the customer's change history." onRetry={onRetry} />
        );
    }

    if (page.amendments.length === 0) {
        return (
            <p className="text-sm text-mp-muted">
                No changes to this customer's details have been recorded yet.
            </p>
        );
    }

    return <Table data={page.amendments} columns={COLUMNS} enableSorting={false} getRowId={getRowId} />;
}
